import { IconConfirmDelete } from '@nxsys/assets/icons';
import AppAttachment from '@nxsys/components/AppAttachment';
import AppConfirmationModal from '@nxsys/components/AppConfirmationModal';
import AppDescription from '@nxsys/components/AppDescription';
import Icon from '@nxsys/components/AppIconImage';
import {
  IconDelete,
  IconDownload,
  IconEdit,
} from '@nxsys/components/AppIconSvg';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import type { AppState } from '@nxsys/store';
import { DocumentType } from '@nxsys/types/models/document';
import { Descriptions } from 'antd';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';

type DetailModalProps = {
  isOpen: boolean;
  title: string;
  documentSelected: DocumentType | null;
  toggle: () => void;
  toggleUpdate: (documentSelected?: DocumentType) => void;
  onDeleteDocument: (id: string, cb?: () => void) => void;
};

const DetailModal: React.FC<DetailModalProps> = ({
  title,
  isOpen,
  documentSelected,
  toggle,
  toggleUpdate,
  onDeleteDocument,
}: DetailModalProps) => {
  const { isAgency } = useSelector((state: AppState) => state.appStore);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);

  const onUpdate = () => {
    if (!documentSelected?.id) return;
    toggle();
    toggleUpdate(documentSelected);
  };

  const onDelete = () => {
    setDeleteModalOpen(!isDeleteModalOpen);
  };

  const onConfirm = () => {
    if (documentSelected) {
      onDeleteDocument(documentSelected.id, () => {
        onDelete();
        toggle();
      });
    }
  };

  const agencyRef =
    documentSelected?.agencyRef ||
    documentSelected?.prospectsOnboardingRef?.agency;
  return (
    <AppModalContainer
      title={title}
      isOpen={isOpen}
      toggle={toggle}
      actions={
        isAgency
          ? [
              {
                name: 'Download',
                variant: 'outlined',
                buttonType: 'primary',
                icon: <IconDownload width={20} height={20} color="#00795B" />,
                onClick: () => {
                  if (documentSelected?.documentFile) {
                    window.open(documentSelected.documentFile, '_blank');
                  }
                },
              },
            ]
          : [
              {
                name: 'Delete',
                variant: 'outlined',
                buttonType: 'primary',
                className: 'btn-delete',
                icon: <IconDelete width={20} height={20} />,
                onClick: onDelete,
              },
              {
                name: 'Download',
                variant: 'outlined',
                buttonType: 'primary',
                icon: <IconDownload width={20} height={20} color="#00795B" />,
                onClick: () => {
                  if (documentSelected?.documentFile) {
                    window.open(documentSelected.documentFile, '_blank');
                  }
                },
              },
              {
                name: 'Edit',
                buttonType: 'primary',
                icon: <IconEdit width={20} height={20} color="#FFFFFF" />,
                onClick: onUpdate,
              },
            ]
      }
    >
      <AppDescription
        column={1}
        labelStyle={{ width: 216 }}
        bordered={false}
        colon={false}
        size="middle"
      >
        <Descriptions.Item label="Doc ID">
          {documentSelected?.id || '-'}
        </Descriptions.Item>
        <Descriptions.Item label="Agency Name">
          {agencyRef?.name || '-'}
        </Descriptions.Item>
        <Descriptions.Item label="Document Title">
          {documentSelected?.documentTitle || '-'}
        </Descriptions.Item>
        <Descriptions.Item label="Document Type">
          {documentSelected?.documentType || '-'}
        </Descriptions.Item>
        <Descriptions.Item label="Attachment">
          {documentSelected?.documentFile ? (
            <a
              href={documentSelected.documentFile}
              target="_blank"
              rel="noopener noreferrer"
              style={{ display: 'flex', alignItems: 'center', gap: 4 }}
            >
              <AppAttachment
                urlFile={documentSelected.documentFile}
                title={documentSelected.documentName}
                className="attachment-box"
              />
            </a>
          ) : (
            '-'
          )}
        </Descriptions.Item>
        <Descriptions.Item label="Document Date">
          {documentSelected?.documentDate
            ? new Date(documentSelected.documentDate).toLocaleDateString()
            : '-'}
        </Descriptions.Item>
        <Descriptions.Item label="Expiry Date">
          {documentSelected?.expiryDate
            ? new Date(documentSelected.expiryDate).toLocaleDateString()
            : '-'}
        </Descriptions.Item>
        <Descriptions.Item label="Uploaded By">
          {documentSelected?.updatedBy
            ? `${documentSelected?.updatedBy?.forename} ${documentSelected?.updatedBy?.forename}`
            : '-'}
        </Descriptions.Item>
      </AppDescription>
      <AppConfirmationModal
        open={isDeleteModalOpen}
        title="Delete Document"
        icon={<Icon src={IconConfirmDelete} alt="Delete" />}
        paragraph={
          <p>
            Are you sure want to delete <a>"{documentSelected?.id}"</a>{' '}
            document?
          </p>
        }
        onDeny={onDelete}
        onConfirm={onConfirm}
      />
    </AppModalContainer>
  );
};

export default DetailModal;
